import type { RouterConfig } from '@nuxt/schema';
import type { RouteRecordRaw } from 'vue-router';

import { FUNNELS, LOCALE_CODES } from '../funnels/config';
import type { FunnelId } from '../funnels/config';
import { exclusivePagesFor, pageOverridesFor } from '../funnels/pageOverrides';

function isBaselineRoute(route: RouteRecordRaw): boolean {
  const path = String(route.path ?? '');

  return !FUNNELS.some(funnel => path === `/${funnel}` || path.startsWith(`/${funnel}/`));
}

function baseRouteName(route: RouteRecordRaw): string {
  return String(route.name ?? '').split('___')[0] ?? '';
}

function funnelCopyOf(route: RouteRecordRaw, funnel: FunnelId): RouteRecordRaw {
  const override = pageOverridesFor(funnel)[baseRouteName(route)];

  return {
    ...route,
    name: `${funnel}__${String(route.name ?? route.path)}`,
    path: `/${funnel}${route.path}`,
    ...(override
      ? { component: override.component, meta: { ...route.meta, ...override.meta } }
      : {}),
  } as RouteRecordRaw;
}

function exclusiveRoutesOf(funnel: FunnelId): RouteRecordRaw[] {
  return Object.entries(exclusivePagesFor(funnel)).flatMap(([pageName, page]) =>
    LOCALE_CODES.map(locale => ({
      name: `${funnel}__${pageName}___${locale}`,
      path: `/${funnel}/${locale}/${pageName}`,
      component: page.component,
      meta: page.meta,
    } as RouteRecordRaw)),
  );
}

export default <RouterConfig>{
  routes: (_routes) => {
    const baselineRoutes = _routes.filter(isBaselineRoute);

    const funnelRoutes = FUNNELS.flatMap((funnel: FunnelId) => [
      ...baselineRoutes.map(route => funnelCopyOf(route, funnel)),
      ...exclusiveRoutesOf(funnel),
    ]);

    return [...baselineRoutes, ...funnelRoutes];
  },
};
